<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { InventoryStatus } from '@/constants/InventoryStatus'
import {
  GetCartFromLocalStorageOrApi,
  GetCurrentCartFromLocalStorage,
  GetCurrentCartIdentifiers,
  PayeeAddToCart,
  EventDeleteFromCart,
  EventAddToCart,
} from '../services/CartService'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { Popup } from '@progress/kendo-vue-popup'
import { ScrollView } from '@progress/kendo-vue-scrollview'
import {
  TextBox as KTextBox,
  Checkbox as KCheckBox,
  type CheckboxChangeEvent,
} from '@progress/kendo-vue-inputs'
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '../logger.ts'
import { FormatCurrency } from '../services/currencyFormatter'
import {
  DeleteItemFromCart,
  UpdateItemInCart,
  GetUniqueProductCodes,
  GetFreeShippingGamificationDifferenceMessage,
  CartLocalStorageKey,
} from '../services/CartService'
import {
  ref,
  onMounted,
  onBeforeUnmount,
  computed,
  watch,
  type ComputedRef,
} from 'vue'
import type { IAoeCart, IAoeCartItem } from '../types/CartTypes'
import {
  GetFullProductImageDictionary,
  GetFullProductUrl,
  GetFullProductImageUrl,
  GetCachedProductDetails,
  GetProductDetailsBatch,
} from '../services/SkuService.ts'
import type { IProductExWithSkuExsViewModel } from '../types/SkuTypes'
import {
  GetCartDetailUrl,
  GetImageRootUrl,
  GetOrderTypeId,
  GetKitOrderTypeId,
  GetBeeNumberAuthenicatedUser,
  GetRootUrl,
  GetCheckoutUrl,
  GetBeeNumberSite,
  GetLoginUrl,
  GetHomeOfficeBeeNumber,
  GetShopUrl,
  GetDefaultConsultantPhotoUrl
} from '../stores/SettingsStore.ts'
import { trackViewCart } from '../analytics/index'
import { buildMatomoItemsFromCart } from '../analytics/matomoItemBuilder'
import { arrowLeftIcon } from '@progress/kendo-svg-icons'
import AoeAddToCartButton from '../components/AoeAddToCartButton.vue'
import AoeWishlistImageButton from './AoeWishlistImageButton.vue'
import AoeCartDetailDiscount from './AoeCartDetailDiscount.vue'
import AoeCartPopupEventLocator from './AoeCartPopupEventLocator.vue'
import { useCookies } from 'vue3-cookies'
import {
  GetEventFromLocalStorageOrApi,
  GetEventHosts,
} from '../services/EventService'
import type { IEventHost } from '../types/EventTypes'
import type { IAoeConsultant } from '../types/ConsultantTypes'
import type { FilterContainer } from '../types/FilterTypes'
import { GetConsultantFromLocalStorageOrApi } from '../services/ConsultantService'
import AoeProductReviewStars from './AoeProductReviewStars.vue'
import AoeCartCoupon from './AoeCartCoupon.vue'
import PricePerUnitWeight from './PricePerUnitWeight.vue'
import Swal from 'sweetalert2'
import { CartCreate, CartGet } from '../api/Cart'
import type { IProductAttributes } from '../types/SkuTypes'
import { CartPaymentMethodGetList } from '@/api/Cart'
import { BrainTreeControlFolderName } from '@/services/PaymentGateways/BraintreeService'
import BraintreeApplePayExpressCheckoutEntry from '@/components/PaymentGateways/Braintree/BraintreeApplePayExpressCheckoutEntry.vue'
import { handleError } from '@/services/ErrorService'

const logger = useLogger()

const props = defineProps<{
  ispopover?: boolean
}>()

const emit = defineEmits<{
  closePopover: []
}>()

function closePopoverHandler() {
  logger.debug('closePopoverHandler')
  emit('closePopover')
}

setStoreLogger(logger)

const cart = ref<IAoeCart | null>(null)
const cartLoaded = ref(false)
const productImageUrls = ref<Record<string, string>>({})
const crossSellingProductsDetails = ref<Array<IProductExWithSkuExsViewModel>>(
  [],
)
const crossSellingProductsDetailsSlides = ref<Array<object>>([])
const mobileCrossSellingProductsDetailsSlides = ref<Array<object>>([])

const productAttributesByProductCode = ref<Record<string, IProductAttributes>>(
  {},
)

const cartItemsWithoutDiscountIds: ComputedRef<IAoeCartItem[]> = computed(
  () => {
    if (!cart.value) return []
    return cart.value.items.filter(
      item =>
        !item.itemPoeDiscPlanLevelDiscIds ||
        item.itemPoeDiscPlanLevelDiscIds.length === 0,
    )
  },
)

const isCartEmpty = computed(() => {
  // Solo retorna true si el cart ya se cargó Y (es null O no tiene items)
  const result =
    cartLoaded.value &&
    (!cart.value || !cart.value.items || cart.value.items.length === 0)
  console.log('isCartEmpty computed:', {
    cartLoaded: cartLoaded.value,
    cartValue: cart.value,
    hasItems: cart.value?.items,
    itemsLength: cart.value?.items?.length,
    result,
  })
  return result
})

const fullProductUrlByProductCode = ref<Record<string, string>>({})
const freeShippingMessageText = ref<string>('')
const orderTypeId = ref<string>('')
const rootUrl = ref<string>('')
const paypalCheckoutImageUrl = ref('')
const applePayCheckoutImageUrl = ref('')
const cartDetailUrl = ref<string>('')
const showCheckoutPopup = ref(false)
const showConnectToPartyPopup = ref(false)
const showOnlyConnectToPartyPopup = ref(false)
const { cookies } = useCookies()
const payeeSite = ref<IAoeConsultant | null>()
const eventTitle = ref<string>('')
const eventHost = ref<string>('')
const eventCity = ref<string>('')
const eventState = ref<string>('')
const notOrderWithConsultantChecked = ref<boolean | null>(null)
const showSelectPartyConsultantMessage = ref(false)
const showFreeProductError = ref(false)
const homeOfficeBeeNumber = ref<number>(1)
const defaultConsultantPhotoUrl = ref<string>('')
const popupJustOpened = ref(false)
const isApplePaySupported = ref(false)
const viewCartTracked = ref(false)
let scrollPosition = 0

const braintreeApplePayConfig = ref<
  | {
      systemKeyword: string
      braintreeMerchantId: string
      displayName: string
    }
  | null
>(null)

// Flip this to false to disable the Apple Pay Express checkout in the cart.
const enableApplePayExpressCheckout = false

const applePayExpressRef = ref<{ startExpressCheckout: () => Promise<void> } | null>(
  null,
)

const isMobileViewport = () =>
  typeof window !== 'undefined' && window.innerWidth <= 768

let bodyScrollLocked = false

function lockBodyScroll() {
  if (isMobileViewport() && !bodyScrollLocked) {
    const scrollY = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.overflow = 'hidden'
    bodyScrollLocked = true
  }
}

function unlockBodyScroll() {
  if (bodyScrollLocked) {
    const top = document.body.style.top
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.left = ''
    document.body.style.right = ''
    document.body.style.overflow = ''
    window.scrollTo(0, parseInt(top || '0', 10) * -1)
    bodyScrollLocked = false
  }
}

watch(
  [showCheckoutPopup, showConnectToPartyPopup],
  ([checkout, connectParty]) => {
    if (!checkout && !connectParty) {
      unlockBodyScroll()
    }
  },
)

onBeforeUnmount(() => {
  unlockBodyScroll()
})

onMounted(async () => {
  logger.debug('onMounted')
  orderTypeId.value = await GetOrderTypeId()
  rootUrl.value = await GetRootUrl()
  cartDetailUrl.value = await GetCartDetailUrl()
  const imageRootUrl = await GetImageRootUrl()
  paypalCheckoutImageUrl.value = imageRootUrl + '/contentimages/paypal.svg'
  applePayCheckoutImageUrl.value = imageRootUrl + '/contentimages/Apple_Pay_Mark_RGB_041619.svg'
  homeOfficeBeeNumber.value = await GetHomeOfficeBeeNumber()
  defaultConsultantPhotoUrl.value = await GetDefaultConsultantPhotoUrl()

  const applePaySession = (typeof window !== 'undefined'
    ? (window as any).ApplePaySession
    : undefined) as any
  isApplePaySupported.value =
    !!applePaySession && typeof applePaySession.canMakePayments === 'function'
      ? !!applePaySession.canMakePayments()
      : false
  // --- Auto-add event from querystring ---
  // Fetch eventId from query string using window.location
  const urlParams = new URLSearchParams(window.location.search)
  const eventIdParam =
    urlParams.get('eventId') ||
    urlParams.get('eventid') ||
    urlParams.get('event_id') ||
    urlParams.get('event')
  let eventIdStr: string | undefined
  if (Array.isArray(eventIdParam)) {
    // Take the first non-null, non-undefined string value
    eventIdStr = eventIdParam.find(
      (v): v is string => typeof v === 'string' && v.trim() !== '',
    )
  } else if (typeof eventIdParam === 'string' && eventIdParam.trim() !== '') {
    eventIdStr = eventIdParam
  }

  if (eventIdStr) {
    const eventId = parseInt(eventIdStr)
    const customerBeeNumber = ref<number>(0)

    if (!isNaN(eventId)) {
      // Ensure cart is loaded (Init loads cart)
      await Init(false)
      // Only add if cart does not already have this event
      let cartIdToken =
        cart.value?.aoeCartIdToken ||
        GetCurrentCartIdentifiers()?.aoeCartIdToken ||
        ''
      if (!cartIdToken) {
        const orderTypeIdVal =
          orderTypeId.value ?? (await GetOrderTypeId()) ?? 0
        GetBeeNumberAuthenicatedUser().then(resp => {
          if (resp != null) {
            customerBeeNumber.value = resp
          }
        })
        const beeNumberPayee = await GetBeeNumberSite()
        const cartCreateResp = await CartCreate({
          orderTypeId: orderTypeIdVal,
          beeNumberPayee: beeNumberPayee,
          beeNumberCustomer: customerBeeNumber.value,
        })

        let cartGetResp = null
        if (cartCreateResp && cartCreateResp.aoeCartIdToken) {
          cartGetResp = await CartGet(cartCreateResp.aoeCartIdToken)
        }

        if (cartGetResp && cartGetResp.item) {
          cartIdToken = cartGetResp.item.aoeCartIdToken
          cart.value = cartGetResp.item
        } else {
          // fallback: try local storage
          cart.value = await GetCurrentCartFromLocalStorage()
          cartIdToken = cart.value?.aoeCartIdToken || ''
        }

        const { cookies } = useCookies()
        cookies.set('AoeCartIdToken', cartIdToken)
      }
      if (!cart.value || cart.value.eventId !== eventId) {
        await EventAddToCart({ aoeCartIdToken: cartIdToken, eventId })
        // Refresh cart
        cart.value = await GetCurrentCartFromLocalStorage()
      }
    }
  } else {
    // The event querystring is dropped as soon as the shopper navigates (e.g. into a category),
    // so by the time they reach the cart it is usually gone even though the cart is still linked
    // to the event. Decide "event mode" from the persisted cart, not the URL: if the cart is
    // linked to an event, load it exactly as we do when the querystring is present — i.e. do NOT
    // re-sync the payee to the ambient site consultant.
    const existingCart = GetCurrentCartFromLocalStorage()
    const cartHasEvent = !!(existingCart?.eventId && existingCart.eventId != 0)
    await Init(!cartHasEvent)
  }

  document.body.addEventListener(
    'click',
    event => {
      if (popupJustOpened.value) return

      if (showCheckoutPopup.value || showConnectToPartyPopup.value) {
        const target = event.target as HTMLElement
        if (
          !target.closest(
            '.aoe-checkout-popup-content, .aoe-connect-party-popup-content, .k-popup',
          )
        ) {
          closeCheckoutPopupHandler()
        }
      }
    },
    true,
  )
})

async function updateCartBeeNumberPayeeToMatchBeeNumberSite() {
  logger.debug('AOECartDetail', 'updateCartBeeNumberPayeeToMatchBeeNumberSite')
  if (!cart.value) {
    logger.debug(
      'AOECartDetail',
      'updateCartBeeNumberPayeeToMatchBeeNumberSite',
      'no cart loaded, skipping',
    )
    return
  }
  // Event carts must always be paid to the event's consultant. Never re-sync the payee to the
  // ambient site consultant for an event cart, even when the event param is absent from the URL
  // (e.g. the customer returned to the cart via the basket icon, a bookmark, or a reload). Doing
  // so silently re-credited event orders to the wrong consultant.
  if (cart.value.eventId && cart.value.eventId != 0) {
    logger.debug(
      'AOECartDetail',
      'updateCartBeeNumberPayeeToMatchBeeNumberSite',
      'cart is associated to an event - skipping payee sync',
      { eventId: cart.value.eventId },
    )
    return
  }

  //check if cart changed payee - move to site
  const beeNumberSite = await GetBeeNumberSite()
  logger.debug(
    'AOECartDetail',
    'updateCartBeeNumberPayeeToMatchBeeNumberSite',
    {
      aoeCartIdToken: cart.value.aoeCartIdToken,
      cartBeeNumberPayee: cart.value.beeNumberPayee,
      beeNumberSite,
    },
  )
  if (
    beeNumberSite &&
    cart.value.beeNumberPayee != 0 &&
    cart.value.beeNumberPayee !== beeNumberSite
  ) {
    logger.debug(
      'AOECartDetail',
      'updateCartBeeNumberPayeeToMatchBeeNumberSite',
      'payee mismatch - updating cart payee to match site',
    )
    await PayeeAddToCart({
      aoeCartIdToken: cart.value.aoeCartIdToken,
      beeNumberPayee: beeNumberSite,
    })
  } else {
    logger.debug(
      'AOECartDetail',
      'updateCartBeeNumberPayeeToMatchBeeNumberSite',
      'no update required',
    )
  }
}

async function Init(updateCartPayeeIfRequired : boolean) {
  // Get the authenticated user's beeNumber
  const beeNumberAuth = await GetBeeNumberAuthenicatedUser()

  // Get the cart using the user-specific key
  let tempCart: IAoeCart | null = await GetCurrentCartFromLocalStorage()

  // If this is a kit cart, delete all kit items so they don't appear in the regular cart
  if (tempCart && tempCart.orderTypeId) {
    const kitOrderTypeId = await GetKitOrderTypeId()
    if (tempCart.orderTypeId === kitOrderTypeId && tempCart.items.length > 0) {
      for (const item of tempCart.items) {
        await DeleteItemFromCart({
          aoeCartIdToken: tempCart.aoeCartIdToken,
          lineNumber: item.lineNumber,
        })
      }
      // Refresh cart after deleting kit items
      tempCart = await GetCurrentCartFromLocalStorage()
    }
  }

  if (tempCart) {

    const uniqueProductCodes = GetUniqueProductCodes(tempCart)
    logger.debug('uniqueProductCodes=', uniqueProductCodes)

    if (uniqueProductCodes.length > 0) {
      const productDetails = await GetCachedProductDetails(
        orderTypeId.value,
        uniqueProductCodes,
      )
      const map: Record<string, IProductAttributes> = {}
      for (const detail of productDetails) {
        map[detail.productCode] = detail.attributes
      }
      productAttributesByProductCode.value = map
    }

    productImageUrls.value =
      await GetFullProductImageDictionary(uniqueProductCodes)
    logger.debug('productImageUrls', productImageUrls)

    const uniqueProductDetails = await GetProductDetailsBatch(
      orderTypeId.value,
      uniqueProductCodes,
      true,
    )

    const crossSellingProductsByProductCode: Record<
      string,
      IProductExWithSkuExsViewModel
    > = {}
    for (const uniqueProductDetail of uniqueProductDetails) {
      const crossSells = uniqueProductDetail.crossSellProducts ?? []
      logger.debug(
        'processing crossSells for',
        uniqueProductDetail.productCode,
        crossSells.length,
      )

      for (const crossSell of crossSells) {
        logger.debug('adding crossSell', crossSell.productCode)
        crossSellingProductsByProductCode[crossSell.productCode] = crossSell
      }
    }
    crossSellingProductsDetails.value = Object.values(
      crossSellingProductsByProductCode,
    )
    logger.debug(
      'crossSellingProductsDetails',
      Object.keys(crossSellingProductsByProductCode).length,
      crossSellingProductsDetails.value.length,
    )
    await UpdateFullProductUrlCrossSelling(crossSellingProductsDetails.value)
    await UpdateProductImageUrlCrossSelling(crossSellingProductsDetails.value)

    // Clear cross-selling slides to prevent duplicates
    crossSellingProductsDetailsSlides.value = []
    mobileCrossSellingProductsDetailsSlides.value = []

    let count = 0
    if (crossSellingProductsDetails.value.length > 0) {
      if (crossSellingProductsDetails.value.length > 3) {
        for (let i = 0; i < crossSellingProductsDetails.value.length; i += 3) {
          const slide = [
            {
              position: count,
              product: crossSellingProductsDetails.value.slice(i, i + 3),
            },
          ]
          crossSellingProductsDetailsSlides.value.push(slide)
          count++
        }
      } else {
        const slide = [
          {
            position: 0,
            product: crossSellingProductsDetails.value,
          },
        ]
        crossSellingProductsDetailsSlides.value.push(slide)
      }

      if (crossSellingProductsDetails.value.length > 2) {
        for (let i = 0; i < crossSellingProductsDetails.value.length; i += 2) {
          const slide = [
            {
              position: count,
              product: crossSellingProductsDetails.value.slice(i, i + 2),
            },
          ]
          mobileCrossSellingProductsDetailsSlides.value.push(slide)
          count++
        }
      } else {
        const slide = [
          {
            position: 0,
            product: crossSellingProductsDetails.value,
          },
        ]
        mobileCrossSellingProductsDetailsSlides.value.push(slide)
      }
    }
    freeShippingMessageText.value =
      GetFreeShippingGamificationDifferenceMessage(t, tempCart)
  }

  logger.debug(
    'CrossSelling',
    crossSellingProductsDetailsSlides.value.length,
    mobileCrossSellingProductsDetailsSlides.value.length,
  )

  if (tempCart) await UpdateFullProductUrlDiscountedItems(tempCart)
  cart.value = tempCart

  if (updateCartPayeeIfRequired) {
    await updateCartBeeNumberPayeeToMatchBeeNumberSite()
  }

  cartLoaded.value = true

  braintreeApplePayConfig.value = null

  if (cart.value && enableApplePayExpressCheckout) {
    try {
      const paymentMethods = await CartPaymentMethodGetList(cart.value.aoeCartIdToken)
      const braintreeMethod = paymentMethods?.items?.find(
        pm => pm.controlFolderName === BrainTreeControlFolderName,
      )

      logger.debug('AoeCartDetail', 'braintreeMethod', braintreeMethod)

      const braintreeMerchantId = braintreeMethod?.parameters?.BraintreeMerchantId ?? null
      const displayName = braintreeMethod?.parameters?.DisplayName ?? null

      if (braintreeMethod && braintreeMerchantId && displayName) {
        braintreeApplePayConfig.value = {
          systemKeyword: braintreeMethod.systemKeyword,
          braintreeMerchantId,
          displayName,
        }
      } else {
        braintreeApplePayConfig.value = null
      }
    } catch (error) {
      braintreeApplePayConfig.value = null
      await handleError(error, t)
    }
  }

  if (cart.value && cart.value?.beeNumberPayee != homeOfficeBeeNumber.value)
  {
    GetConsultantFromLocalStorageOrApi(cart.value?.beeNumberPayee).then((consultant) => {
      payeeSite.value = consultant
    })
  }

  GetEvent()

  if (
    !props.ispopover &&
    !viewCartTracked.value &&
    cart.value &&
    cart.value.items.length > 0
  ) {
    viewCartTracked.value = true
    const matomoItems = await buildMatomoItemsFromCart(
      cart.value,
      orderTypeId.value,
    )
    trackViewCart(
      matomoItems,
      cart.value.total ?? 0,
      cart.value.currencyId || 'EUR',
    )
  }
}

function GetEvent() {
  if (cart.value && cart.value?.eventIdToken != undefined) {
    GetEventFromLocalStorageOrApi({
      beeNumber: cart.value.beeNumberPayee,
      eventIdToken: cart.value.eventIdToken,
    }).then(event => {
      if (event) {
        eventTitle.value = event.title
        eventHost.value = event.hostName
        eventCity.value = event.eventCity
        eventState.value = event.eventState
      }
    })
  } else {
    eventTitle.value = ''
    eventHost.value = ''
    eventCity.value = ''
    eventState.value = ''
  }
}

async function UpdateProductImageUrlCrossSelling(
  crossSellingProductsDetails: Array<IProductExWithSkuExsViewModel>,
) {
  logger.debug('UpdateProductImageUrlCrossSelling')
  for (const crossSellingProductDetail of crossSellingProductsDetails) {
    let productImageUrl =
      productImageUrls.value[crossSellingProductDetail.productCode]

    if (productImageUrl) continue
    productImageUrl = await GetFullProductImageUrl(
      crossSellingProductDetail.categoryCode,
      crossSellingProductDetail.attributes.CatalogImage,
    )
    productImageUrls.value[crossSellingProductDetail.productCode] =
      productImageUrl
  }
}
async function UpdateFullProductUrlCrossSelling(
  crossSellingProductsDetails: Array<IProductExWithSkuExsViewModel>,
) {
  logger.debug('UpdateFullProductUrlCrossSelling')
  for (const crossSellingProductDetail of crossSellingProductsDetails) {
    let fullProductUrl =
      fullProductUrlByProductCode.value[crossSellingProductDetail.productCode]
    if (fullProductUrl) continue
    fullProductUrl = await GetFullProductUrl(
      crossSellingProductDetail.categoryCode,
      crossSellingProductDetail.productCode,
    )
    fullProductUrlByProductCode.value[crossSellingProductDetail.productCode] =
      fullProductUrl
  }
}
async function UpdateFullProductUrlDiscountedItems(cart: IAoeCart) {
  logger.debug('UpdateFullProductUrlDiscountedItems')
  for (const cartItem of cart.items.filter(
    item =>
      !item.poeDiscPlanLevelDiscIds ||
      item.poeDiscPlanLevelDiscIds.length === 0,
  )) {
    let fullProductUrl = fullProductUrlByProductCode.value[cartItem.productCode]
    if (fullProductUrl) continue
    fullProductUrl = await GetFullProductUrl(
      cartItem.categoryCode,
      cartItem.productCode,
    )
    fullProductUrlByProductCode.value[cartItem.productCode] = fullProductUrl
  }
}

async function storageHandler(event: StorageEvent) {
  logger.debug('AOECartDetail', 'storageHandler', event.key)
  const cartIdentifiers = GetCurrentCartIdentifiers()

  // Check if the event is for the cart storage key or a user-specific key
  if (
    cartIdentifiers &&
    (event.key === CartLocalStorageKey ||
      event.key?.startsWith(CartLocalStorageKey + '_'))
  ) {
    // Get the authenticated user's beeNumber
    const beeNumberAuth = await GetBeeNumberAuthenicatedUser()

    cart.value = await GetCartFromLocalStorageOrApi(
      cartIdentifiers.aoeCartIdToken,
    )
    if (cart.value) {
      freeShippingMessageText.value =
        GetFreeShippingGamificationDifferenceMessage(t, cart.value)

      await Init(false)
    }
  }
}

window.addEventListener('storage', storageHandler, false)

async function GetAvailableQty(cartItem: IAoeCartItem, currentCartQty: number) {
  const productDetails = await GetCachedProductDetails(orderTypeId.value, [
    cartItem.skuCode,
  ])
  if (
    productDetails == null ||
    productDetails.length == 0 ||
    productDetails[0].skus == null ||
    productDetails[0].skus.length == 0 ||
    productDetails[0].skus[0].inventoryLevel == undefined
  )
    return 0
  return productDetails[0].skus[0].inventoryLevel.availableQty - currentCartQty
}

async function plusClickedHandler(lineNumber: number) {
  logger.debug('plusClickedHandler', lineNumber)
  if (!cart.value) {
    logger.warn('cart is null')
    return
  }
  const cartItem = cart.value?.items.find(
    item => item.lineNumber === lineNumber,
  )
  if (!cartItem) {
    logger.warn(
      'cartItem is null for aoeCartId',
      cart.value?.aoeCartId,
      'linenumber',
      lineNumber,
    )
    return
  }
  let quantity = 1
  if (cartItem.quantity) quantity = cartItem.quantity + 1

  let canAddToCart = true

  // Skip availability check if OutOfStockVisible is true
  if (!cartItem.outOfStockVisible) {
    const availableQty = await GetAvailableQty(cartItem, cartItem.quantity)
    if (availableQty < 1) {
      canAddToCart = false
    }
  }

  if (canAddToCart) {
    try {
      await UpdateItemInCart({
        aoeCartIdToken: cart.value.aoeCartIdToken,
        lineNumber,
        quantity,
      })
    } catch (error) {
      logger.error('plusClickedHandler UpdateItemInCart failed', error)
      await handleError(error, t)
    }

    // Get the authenticated user's beeNumber
    const beeNumberAuth = await GetBeeNumberAuthenicatedUser()
    cart.value = await GetCartFromLocalStorageOrApi(cart.value.aoeCartIdToken)
  } else {
    Swal.fire({
      title: t('alerts.AddToCartError'),
      text: t('alerts.NoQuantityAvailable'),
      icon: 'error',
      confirmButtonText: t('buttons.Okay'),
    })

    // Get the authenticated user's beeNumber
    const beeNumberAuth = await GetBeeNumberAuthenicatedUser()
    cart.value = await GetCartFromLocalStorageOrApi(cart.value.aoeCartIdToken)
  }
}

async function deleteItemClickedHandler(lineNumber: number) {
  logger.debug('deleteItemClickedHandler', lineNumber)
  if (!cart.value) {
    logger.warn('cart is null')
    return
  }
  const removedItem = cart.value.items.find(i => i.lineNumber === lineNumber)
  let deleteSucceeded = true
  try {
    await DeleteItemFromCart({
      aoeCartIdToken: cart.value.aoeCartIdToken,
      lineNumber,
    })
  } catch (error) {
    deleteSucceeded = false
    logger.error('deleteItemClickedHandler DeleteItemFromCart failed', error)
    await handleError(error, t)
  }

  // Get the authenticated user's beeNumber
  const beeNumberAuth = await GetBeeNumberAuthenicatedUser()
  cart.value = await GetCartFromLocalStorageOrApi(cart.value.aoeCartIdToken)

  if (removedItem && deleteSucceeded) {
    Swal.fire({
      title: t('cart.ItemRemovedFromCart', { skuName: removedItem.skuName }),
      icon: 'info',
      timer: 2000,
      showConfirmButton: false,
      position: 'top-end',
      toast: true
    })
  }
}

async function minusClickedHandler(lineNumber: number) {
  logger.debug('minusClickedHandler', lineNumber)
  if (!cart.value) {
    logger.warn('cart is null')
    return
  }
  const cartItem = cart.value?.items.find(
    item => item.lineNumber === lineNumber,
  )
  if (!cartItem) {
    logger.warn(
      'cartItem is null for aoeCartId',
      cart.value?.aoeCartId,
      'linenumber',
      lineNumber,
    )
    return
  }
  if (cartItem.quantity && cartItem.quantity > 1) {
    const quantity = cartItem.quantity - 1
    try {
      await UpdateItemInCart({
        aoeCartIdToken: cart.value.aoeCartIdToken,
        lineNumber,
        quantity,
      })
    } catch (error) {
      logger.error('minusClickedHandler UpdateItemInCart failed', error)
      await handleError(error, t)
    }

    // Get the authenticated user's beeNumber
    const beeNumberAuth = await GetBeeNumberAuthenicatedUser()
    cart.value = await GetCartFromLocalStorageOrApi(cart.value.aoeCartIdToken)
  }
}

async function cartItemQuantityChangedHandler(
  event: unknown,
  cartItem: IAoeCartItem,
) {
  logger.debug('cartItemQuantityChangedHandler', event, cartItem)
  if (!cart.value) {
    logger.warn('cart is null')
    return
  }

  let canAddToCart = true

  // Skip availability check if OutOfStockVisible is true
  if (!cartItem.outOfStockVisible) {
    const availableQty = await GetAvailableQty(cartItem, 0)
    if (availableQty < cartItem.quantity) {
      canAddToCart = false
    }
  }

  // Get the authenticated user's beeNumber
  const beeNumberAuth = await GetBeeNumberAuthenicatedUser()

  if (canAddToCart && cartItem.quantity > 0) {
    try {
      await UpdateItemInCart({
        aoeCartIdToken: cart.value.aoeCartIdToken,
        lineNumber: cartItem.lineNumber,
        quantity: cartItem.quantity,
      })
    } catch (error) {
      logger.error('cartItemQuantityChangedHandler UpdateItemInCart failed', error)
      await handleError(error, t)
    }

    cart.value = await GetCartFromLocalStorageOrApi(cart.value.aoeCartIdToken)
  } else if (cartItem.quantity === 0) {
    logger.warn('cartItem.quantity is 0')
    cart.value = await GetCartFromLocalStorageOrApi(cart.value.aoeCartIdToken)
  } else {
    Swal.fire({
      title: t('alerts.AddToCartError'),
      text: t('alerts.NoQuantityAvailable'),
      icon: 'error',
      confirmButtonText: t('buttons.Okay'),
    })

    cart.value = await GetCartFromLocalStorageOrApi(cart.value.aoeCartIdToken)
  }
}

async function cartItemQuantityInputHandler(
  event: { value: string },
  cartItem: IAoeCartItem,
) {
  logger.debug('cartItemQuantityInputHandler', event, cartItem, event.value)
  if (event.value === null || event.value.length === 0) {
    logger.warn('event.value is undefined')
    cartItem.quantity = 0
    return
  }
  const quantity = parseInt(event.value)
  logger.debug('quantity', quantity)
  if (quantity > 0) {
    cartItem.quantity = quantity
  }
}

function GetFullProductImageUrlFromStringDictionary(productCode: string) {
  logger.debug(
    'GetFullProductImageUrlFromStringDictionary',
    productCode,
    productImageUrls,
  )
  const productImageUrl = productImageUrls.value[productCode]
  logger.debug('productImageUrl=', productImageUrl)
  if (productImageUrl) return productImageUrl
  return '#'
}

async function continueShoppingClickedHandler(event: MouseEvent) {
  logger.debug('continueShoppingClickedHandler', event, rootUrl.value)

  window.location.href = await GetShopUrl()
}

function viewCartClickedHandler() {
  logger.debug('viewCartClickedHandler')
  if (cartDetailUrl.value) window.location.href = cartDetailUrl.value
}

function getCrossSellRetailPrice(
  crossSellingProductsDetail: IProductExWithSkuExsViewModel,
): number {
  if (
    crossSellingProductsDetail &&
    crossSellingProductsDetail.skus &&
    crossSellingProductsDetail.skus.length > 0
  ) {
    return crossSellingProductsDetail.skus[0].retailPrice
  }
  return 0
}

function getCrossSellSkuCode(
  crossSellingProductsDetail: IProductExWithSkuExsViewModel,
): string {
  if (
    crossSellingProductsDetail &&
    crossSellingProductsDetail.skus &&
    crossSellingProductsDetail.skus.length > 0
  ) {
    return crossSellingProductsDetail.skus[0].skuCode
  }
  return ''
}

function getCrossSellAvailableQuantity(
  crossSellingProductsDetail: IProductExWithSkuExsViewModel,
): string {
  if (
    crossSellingProductsDetail &&
    crossSellingProductsDetail.skus &&
    crossSellingProductsDetail.skus.length > 0 &&
    crossSellingProductsDetail.skus[0].inventoryLevel != undefined
  ) {
    if (crossSellingProductsDetail.skus[0].inventoryLevel != undefined) {
      return crossSellingProductsDetail.skus[0].inventoryLevel.availableQty.toString()
    }
  }
  return '0'
}

function getCrossSellOutOfStockVisible(
  crossSellingProductsDetail: IProductExWithSkuExsViewModel,
): boolean {
  if (
    crossSellingProductsDetail &&
    crossSellingProductsDetail.skus &&
    crossSellingProductsDetail.skus.length > 0
  ) {
    return crossSellingProductsDetail.skus[0].outOfStockVisible === true
  }
  return false
}

function GetFullProductUrlFromStringDictionary(productCode: string): string {
  const fullProductUrl = fullProductUrlByProductCode.value[productCode]
  if (fullProductUrl) {
    logger.debug('productCode=', productCode, 'fullProductUrl=', fullProductUrl)
  } else {
    console.warn('productCode=', productCode, 'fullProductUrl=', fullProductUrl)
  }
  return fullProductUrl || '#'
}

async function checkoutClickedHandler() {
  logger.debug('checkoutClickedHandler')

  if (!notOrderWithConsultantChecked.value) {
    if (
      !cart.value ||
      cart.value.beeNumberPayee == homeOfficeBeeNumber.value ||
      cart.value.beeNumberPayee == null
    ) {
      showSelectPartyConsultantMessage.value = true
      return
    }
  }

  if (!hasSelectedOrOptedOutFreeProduct()) {
    showFreeProductError.value = true
    return
  } else {
    showFreeProductError.value = false
  }

  showSelectPartyConsultantMessage.value = false

  const beeNumberAuthenicatedUser = await GetBeeNumberAuthenicatedUser()
  if (beeNumberAuthenicatedUser) {
    window.location.href = await GetCheckoutUrl()
  } else {
    logger.debug('checkoutClickedHandler no one is logged in')
    showCheckoutPopup.value = true
    logger.debug(
      'checkoutClickedHandler no one is logged in: ',
      showCheckoutPopup,
    )

    popupJustOpened.value = true
    setTimeout(() => {
      popupJustOpened.value = false
    }, 300)

    setTimeout(() => {
      const popupElement = document.querySelector('.aoe-checkout-popup-content')
      if (popupElement) {
        if (isMobileViewport()) {
          popupElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          setTimeout(() => {
            scrollPosition =
              window.pageYOffset || document.documentElement.scrollTop
            lockBodyScroll()
          }, 500)
        } else {
          popupElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          setTimeout(() => {
            scrollPosition =
              window.pageYOffset || document.documentElement.scrollTop
          }, 500)
        }
      }
    }, 100)
  }
}

async function applePayCheckoutClickedHandler() {
  logger.debug('applePayCheckoutClickedHandler')

  if (!applePayExpressRef.value) {
    return
  }
  await applePayExpressRef.value.startExpressCheckout()
}

async function checkoutGuestClickedHandler()
{
  logger.debug('checkoutGuestClickedHandler')
  window.location.href = await GetCheckoutUrl()
}

function closeCheckoutPopupHandler() {
  logger.debug('closeCheckoutPopupHandler')
  showCheckoutPopup.value = false
  showConnectToPartyPopup.value = false
  window.scrollTo(0, scrollPosition)
}

async function signInClickedHandler() {
  logger.debug('signInClickedHandler')
  const returnUrl = await GetCheckoutUrl()
  window.location.href =
    (await GetLoginUrl()) + '?returnUrl=' + encodeURIComponent(returnUrl) + "&nlt=true"
}

const eventHosts = ref<IEventHost[]>([])

async function findPartyOrConsultantBtnHandler() {
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  const beeNumberSite = await GetBeeNumberSite()
  if (eventHosts.value.length == 0 && !props.ispopover) {
    // Calculate date 6 months ago
    const sixMonthsAgo = new Date()
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)
    const sixMonthsAgoISO = sixMonthsAgo.toISOString()

    // Create filter for events within last 6 months
    const filterContainer: FilterContainer = {
      Where: {
        OperatorType: 'Or',
        Operands: [
          {
            Field: 'EventStartDate',
            Value: sixMonthsAgoISO,
            FilterType: 'GreaterThanOrEqual',
          },
        ],
      },
    }

    GetEventHosts({
      consultantBeeNumber: beeNumberSite,
      responseFilter: filterContainer,
      isHomeOffice: Number(beeNumberSite) === Number(homeOfficeBeeNumber.value),
    }).then(eventHostsResp => {
      logger.debug(
        homeOfficeBeeNumber.value,
        beeNumberSite,
        typeof homeOfficeBeeNumber.value,
        typeof beeNumberSite,
      )
      for (const eventHost of eventHostsResp.items) {
        const eH = eventHost as IEventHost
        eH.hostName = eH.firstName + ' ' + eH.lastName

        const date = new Date(eH.eventDate)
        const options: Intl.DateTimeFormatOptions = {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }
        eH.eventDate = date.toLocaleString(navigator.language, options)
        eH.aoeCartIdToken = cart.value?.aoeCartIdToken || ''
        eventHosts.value.push(eH)
      }
      logger.debug('eventHosts', eventHosts)
    })
  }

  showConnectToPartyPopup.value = true

  popupJustOpened.value = true
  setTimeout(() => {
    popupJustOpened.value = false
  }, 300)

  setTimeout(() => {
    const popupElement = document.querySelector(
      '.aoe-connect-party-popup-content',
    )
    if (popupElement) {
      if (isMobileViewport()) {
        popupElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setTimeout(() => {
          scrollPosition =
            window.pageYOffset || document.documentElement.scrollTop
          lockBodyScroll()
        }, 500)
      } else {
        popupElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setTimeout(() => {
          scrollPosition =
            window.pageYOffset || document.documentElement.scrollTop
        }, 500)
      }
    }
  }, 100)
}

function removeConsultantHandler() {
  logger.debug('removeConsultantHandler')
  GetHomeOfficeBeeNumber().then(homeOfficeBeeNumber => {
    PayeeAddToCart({
      aoeCartIdToken: cart.value?.aoeCartIdToken || '',
      beeNumberPayee: homeOfficeBeeNumber,
    }).then(() => {
      redirectToSite(homeOfficeBeeNumber)
    })
  })
}

function redirectToSite(beeNumber: number) {
  logger.debug('AOECartDetail', 'redirectToSite', beeNumber)
  cookies.set('ReplicatedSiteBeeNumber', beeNumber.toString())
  logger.debug(
    'AOECartDetail',
    'redirectToSite',
    'redirecting via window.location.href refresh',
  )
  window.location.href = window.location.href
}

function removeEventHandler() {
  logger.debug('removeEventHandler')
  EventDeleteFromCart({
    aoeCartIdToken: cart.value?.aoeCartIdToken || '',
  }).then(() => {
    GetEvent()
  })
}

function onNotOrderWithConsultantChange(event: CheckboxChangeEvent) {
  notOrderWithConsultantChecked.value = event.value

  if (event.value && cart.value) {
    if (
      cart.value.beeNumberPayee != homeOfficeBeeNumber.value &&
      cart.value.beeNumberPayee != null
    ) {
      removeConsultantHandler()
    }
    if (cart.value.eventId && cart.value.eventId != 0) {
      removeEventHandler()
    }
  }
}

const noFreeGiftOptedOut = ref<boolean>(false)
const freeItemSelected = ref<boolean>(false)

function onFreeGiftOptedOut(poeDiscPlanLevelDiscId: number) {
  logger.debug('onFreeGiftOptedOut', poeDiscPlanLevelDiscId)
  noFreeGiftOptedOut.value = true
  freeItemSelected.value = false
}

function onFreeItemSelected(poeDiscPlanLevelDiscId: number) {
  logger.debug('onFreeItemSelected', poeDiscPlanLevelDiscId)
  noFreeGiftOptedOut.value = false
  freeItemSelected.value = true
}

function showFreeGiftsAgain() {
  noFreeGiftOptedOut.value = false
  freeItemSelected.value = false
}

function hasSelectedOrOptedOutFreeProduct(): boolean {
  if (
    !cart.value ||
    !Array.isArray(cart.value.earnedDiscounts) ||
    cart.value.earnedDiscounts.length === 0
  )
    return true

  // Check if SelectFreeProduct is one of the earned discounts
  const hasSelectFreeProduct = cart.value.earnedDiscounts.some(
    d => d.systemKeyword === 'SelectFreeProduct',
  )

  // Only check freeItemSelected and noFreeGiftOptedOut if SelectFreeProduct is earned
  if (hasSelectFreeProduct) {
    logger.debug(
      'hasSelectedOrOptedOutFreeProduct',
      freeItemSelected.value,
      noFreeGiftOptedOut.value,
    )
    return freeItemSelected.value || noFreeGiftOptedOut.value
  }

  // If SelectFreeProduct is not earned, return true
  return true
}
</script>

<template>
  <div
    class="aoe-cart-detail aoe-cart-detail-container container"
    data-testid="cart-detail-container"
  >
    <!-- Overlay for popup -->
    <div
      v-if="showCheckoutPopup || showConnectToPartyPopup"
      class="aoe-cart-popup-overlay"
      @click="closeCheckoutPopupHandler"
    ></div>
    <div v-if="props.ispopover" class="cart-header-with-close">
      <button
        class="close-cart-button"
        @click="closePopoverHandler"
        :aria-label="t('cart.CloseCart')"
        data-testid="close-cart-button"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>
      <p class="pageHeader">{{ t('cart.CartHeader') }}</p>
    </div>
    <p v-else class="pageHeader">{{ t('cart.CartHeader') }}</p>
    <div class="row" v-if="cart">
      <div
        :class="
          props.ispopover
            ? 'col-12 allCartItems'
            : 'col-12 col-md-8 allCartItems'
        "
      >
        <AoeCartDetailDiscount
          :cart="cart"
          @free-gift-opted-out="onFreeGiftOptedOut"
          @free-gift-selected="onFreeItemSelected"
          @show-free-gifts-again="showFreeGiftsAgain"
          data-testid="cart-discount-component"
        />
        <div
          class="cartItems"
          v-for="cartItem in cartItemsWithoutDiscountIds"
          :key="cartItem.lineNumber"
          :data-testid="`cart-item-${cartItem.lineNumber}`"
        >
          <div class="row mobileCartItems">
            <div class="product-image col-12 col-sm-3">
              <a
                :href="
                  GetFullProductUrlFromStringDictionary(cartItem.productCode)
                "
                :data-category-code="cartItem.productCode"
              >
                <img
                  :src="
                    GetFullProductImageUrlFromStringDictionary(
                      cartItem.productCode,
                    )
                  "
                  class="img-fluid prodImage"
                  :alt="t('cart.ProductImageAlt')"
                />
              </a>
            </div>
            <div class="col-sm-9 col-12 cartItemInfoMobile">
              <div class="row">
                <div class="col-10">
                  <div class="cartSkuName">{{ cartItem.skuName }}</div>
                  <div class="cartSkuCode">#{{ cartItem.skuCode }}</div>
                </div>
                <div class="col-1">
                  <KButton
                    :fill-mode="'clear'"
                    @click="deleteItemClickedHandler(cartItem.lineNumber)"
                    class="deleteItemButton"
                    :data-testid="`delete-item-mobile-${cartItem.lineNumber}`"
                    >&#x2715;</KButton
                  >
                </div>
              </div>
              <div class="row">
                <div class="col-7 cartItemInfoMobileQty">
                  <div class="number">
                    <button
                      class="minus"
                      @click="minusClickedHandler(cartItem.lineNumber)"
                      :data-testid="`quantity-minus-mobile-${cartItem.lineNumber}`"
                    >
                      &minus;
                    </button>
                    <KTextBox
                      class="quantity"
                      :model-value="cartItem.quantity"
                      @change="cartItemQuantityChangedHandler($event, cartItem)"
                      @input="cartItemQuantityInputHandler($event, cartItem)"
                      :data-testid="`quantity-input-mobile-${cartItem.lineNumber}`"
                    />
                    <button
                      class="plus"
                      @click="plusClickedHandler(cartItem.lineNumber)"
                      :data-testid="`quantity-plus-mobile-${cartItem.lineNumber}`"
                    >
                      &plus;
                    </button>
                  </div>
                </div>
                <div class="col-5">
                  <div
                    class="cartItemPrice"
                    :data-testid="`item-price-mobile-${cartItem.lineNumber}`"
                  >
                    {{ FormatCurrency(cartItem.price, null) }}
                  </div>
                  <PricePerUnitWeight
                    :RetailPrice="cartItem.retailPrice"
                    :SalePrice="null"
                    :IProductAttributes="
                      productAttributesByProductCode[cartItem.productCode] ??
                      null
                    "
                  />
                </div>
              </div>
            </div>
          </div>

          <div v-if="!props.ispopover" class="row nonMobileCartItems">
            <div class="product-image col-3">
              <a
                :href="
                  GetFullProductUrlFromStringDictionary(cartItem.productCode)
                "
                :data-category-code="cartItem.productCode"
              >
                <img
                  :src="
                    GetFullProductImageUrlFromStringDictionary(
                      cartItem.productCode,
                    )
                  "
                  class="img-fluid prodImage"
                  :alt="t('cart.ProductImageAlt')"
                />
              </a>
            </div>
            <div class="col-3 cartItemInfo">
              <div class="cartSkuName">{{ cartItem.skuName }}</div>
              <div class="cartSkuCode">#{{ cartItem.skuCode }}</div>
            </div>
            <div class="col-3 cartItemInfoQty">
              <div class="row">
                <div class="col-12">
                  <div class="number">
                    <button
                      class="minus"
                      @click="minusClickedHandler(cartItem.lineNumber)"
                      :data-testid="`quantity-minus-desktop-${cartItem.lineNumber}`"
                    >
                      &minus;
                    </button>
                    <KTextBox
                      class="quantity"
                      :model-value="cartItem.quantity"
                      @change="cartItemQuantityChangedHandler($event, cartItem)"
                      @input="cartItemQuantityInputHandler($event, cartItem)"
                      :data-testid="`quantity-input-desktop-${cartItem.lineNumber}`"
                    />
                    <button
                      class="plus"
                      @click="plusClickedHandler(cartItem.lineNumber)"
                      :data-testid="`quantity-plus-desktop-${cartItem.lineNumber}`"
                    >
                      &plus;
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-2 cartItemInfo">
              <div
                class="cartItemPrice"
                :data-testid="`item-price-desktop-${cartItem.lineNumber}`"
              >
                {{ FormatCurrency(cartItem.price, null) }}
              </div>
              <PricePerUnitWeight
                :RetailPrice="cartItem.retailPrice"
                :SalePrice="null"
                :IProductAttributes="
                  productAttributesByProductCode[cartItem.productCode] ?? null
                "
              />
            </div>
            <div class="col-1 cartItemInfo">
              <KButton
                :fill-mode="'clear'"
                @click="deleteItemClickedHandler(cartItem.lineNumber)"
                class="deleteItemButton"
                :data-testid="`delete-item-desktop-${cartItem.lineNumber}`"
              >
                <span>&#x2715;</span>
                <span class="sr-only">{{ t('cart.RemoveFromCart') }}</span>
              </KButton>
            </div>
          </div>

          <div v-if="props.ispopover" class="row mt-2 nonMobileCartItems">
            <div class="product-image col-3">
              <a
                :href="
                  GetFullProductUrlFromStringDictionary(cartItem.productCode)
                "
                :data-category-code="cartItem.productCode"
              >
                <img
                  :src="
                    GetFullProductImageUrlFromStringDictionary(
                      cartItem.productCode,
                    )
                  "
                  class="img-fluid prodImage"
                  :alt="t('cart.ProductImageAlt')"
                />
              </a>
            </div>
            <div class="col-5 popOverCartItemInfoSecond">
              <div class="cartSkuName">{{ cartItem.skuName }}</div>
              <div class="cartSkuCode">#{{ cartItem.skuCode }}</div>
              <div class="number">
                <button
                  class="minus"
                  @click="minusClickedHandler(cartItem.lineNumber)"
                  :data-testid="`quantity-minus-popover-${cartItem.lineNumber}`"
                >
                  &minus;
                </button>
                <KTextBox
                  class="quantity"
                  :model-value="cartItem.quantity"
                  @change="cartItemQuantityChangedHandler($event, cartItem)"
                  @input="cartItemQuantityInputHandler($event, cartItem)"
                  :data-testid="`quantity-input-popover-${cartItem.lineNumber}`"
                />
                <button
                  class="plus"
                  @click="plusClickedHandler(cartItem.lineNumber)"
                  :data-testid="`quantity-plus-popover-${cartItem.lineNumber}`"
                >
                  &plus;
                </button>
              </div>
            </div>
            <div class="col-3 popOverCartItemInfoThird">
              <div class="popOverDeleteItemButtonDiv">
                <KButton
                  :fill-mode="'clear'"
                  @click="deleteItemClickedHandler(cartItem.lineNumber)"
                  class="popOverDeleteItemButton"
                  :data-testid="`delete-item-popover-${cartItem.lineNumber}`"
                  >&#x2715;</KButton
                >
              </div>
              <div
                class="cartItemPrice"
                :data-testid="`item-price-popover-${cartItem.lineNumber}`"
              >
                {{ FormatCurrency(cartItem.price, null) }}
              </div>
              <PricePerUnitWeight
                :RetailPrice="cartItem.retailPrice"
                :SalePrice="null"
                :IProductAttributes="
                  productAttributesByProductCode[cartItem.productCode] ?? null
                "
              />
            </div>
          </div>
        </div>

        <div v-if="!props.ispopover" class="row pt-5">
          <div class="col">
            <KButton
              :svg-icon="arrowLeftIcon"
              :rounded="'large'"
              :fill-mode="'outline'"
              @click="continueShoppingClickedHandler($event)"
              class="continueShoppingButton"
              data-testid="continue-shopping-btn"
              >{{ t('cart.ContinueShopping') }}</KButton
            >
          </div>
        </div>

        <div
          v-if="
            !props.ispopover &&
            mobileCrossSellingProductsDetailsSlides.length > 0
          "
          class="row pt-5 mobileCrossSell"
        >
          <div class="col">
            <ScrollView
              :data-items="mobileCrossSellingProductsDetailsSlides"
              :content="'content'"
              :automatic-view-change="false"
            >
              <template v-slot:content="{ props }">
                <div class="image-with-text">
                  <p class="pageHeader">{{ t('cart.RelatedProducts') }}</p>
                  <div class="row">
                    <div
                      class="col-6 crossSellProdInfo"
                      v-for="crossSellingProductsDetail in props.item[0]
                        .product"
                      :key="crossSellingProductsDetail.productCode"
                    >
                      <a
                        :href="
                          GetFullProductUrlFromStringDictionary(
                            crossSellingProductsDetail.productCode,
                          )
                        "
                        :data-category-code="
                          crossSellingProductsDetail.productCode
                        "
                        draggable="false"
                      >
                        <img
                          :src="
                            GetFullProductImageUrlFromStringDictionary(
                              crossSellingProductsDetail.productCode,
                            )
                          "
                          class="crossSellImage"
                          :alt="t('cart.ProductImageAlt')"
                          draggable="false"
                        />
                      </a>
                      <div class="row">
                        <div class="col-12 crossSellProductName">
                          {{ crossSellingProductsDetail.productName }}
                        </div>
                      </div>
                      <div class="col-12 crossSellPrice">
                        {{
                          FormatCurrency(
                            getCrossSellRetailPrice(crossSellingProductsDetail),
                            null,
                          )
                        }}
                      </div>
                      <div class="col-12">
                        <AoeAddToCartButton
                          :ordertypeid="props.ordertypeid"
                          :skucode="
                            getCrossSellSkuCode(crossSellingProductsDetail)
                          "
                          :availablequantity="
                            getCrossSellAvailableQuantity(
                              crossSellingProductsDetail,
                            )
                          "
                          :outofstockvisible="
                            getCrossSellOutOfStockVisible(
                              crossSellingProductsDetail,
                            )
                          "
                          :displayquantityspinner="'false'"
                          :disabled="
                            !getCrossSellOutOfStockVisible(crossSellingProductsDetail) &&
                            crossSellingProductsDetail.skus &&
                            crossSellingProductsDetail.skus[0].inventoryLevel &&
                            (crossSellingProductsDetail.skus[0].inventoryLevel
                              .inventoryLevel ===
                              InventoryStatus.OUT_OF_STOCK ||
                              crossSellingProductsDetail.skus[0].inventoryLevel
                                .inventoryLevel === InventoryStatus.STOP_SELL)
                          "
                        ></AoeAddToCartButton>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </ScrollView>
          </div>
        </div>
        <div
          v-if="
            !props.ispopover && crossSellingProductsDetailsSlides.length > 0
          "
          class="row pt-5 crosssell"
        >
          <div class="col">
            <ScrollView
              :data-items="crossSellingProductsDetailsSlides"
              :content="'content'"
              :automatic-view-change="false"
            >
              <template v-slot:content="{ props }">
                <div class="image-with-text">
                  <p class="pageHeader scrollView">
                    {{ t('cart.RelatedProducts') }}
                  </p>
                  <div class="row">
                    <div
                      class="col-4 crossSellProdInfo"
                      v-for="crossSellingProductsDetail in props.item[0]
                        .product"
                      :key="crossSellingProductsDetail.productCode"
                    >
                      <a
                        :href="
                          GetFullProductUrlFromStringDictionary(
                            crossSellingProductsDetail.productCode,
                          )
                        "
                        :data-category-code="
                          crossSellingProductsDetail.productCode
                        "
                        draggable="false"
                      >
                        <img
                          :src="
                            GetFullProductImageUrlFromStringDictionary(
                              crossSellingProductsDetail.productCode,
                            )
                          "
                          class="crossSellImage"
                          :alt="t('cart.ProductImageAlt')"
                          draggable="false"
                        />
                      </a>
                      <div class="row">
                        <div class="col-12 crossSellProductName">
                          {{ crossSellingProductsDetail.productName }}
                        </div>
                      </div>
                      <div class="row crossSellReviewStars">
                        <div class="col-12 crossSellProductCode">
                          <AoeProductReviewStars
                            :productcode="
                              crossSellingProductsDetail.productCode
                            "
                            :usecolor="false"
                            :showtotal="true"
                            starsize="medium"
                          >
                          </AoeProductReviewStars>
                        </div>
                      </div>
                      <div
                        class="wishListBtnArea"
                        data-testid="wishlist-button-area"
                      >
                        <AoeWishlistImageButton
                          :skucode="
                            crossSellingProductsDetail.skus[0]?.skuCode || ''
                          "
                          data-testid="wishlist-btn"
                        ></AoeWishlistImageButton>
                      </div>
                      <div class="col-12 crossSellPrice">
                        {{
                          FormatCurrency(
                            getCrossSellRetailPrice(crossSellingProductsDetail),
                            null,
                          )
                        }}
                      </div>
                      <div class="col-12">
                        <AoeAddToCartButton
                          :ordertypeid="props.ordertypeid"
                          :skucode="
                            getCrossSellSkuCode(crossSellingProductsDetail)
                          "
                          :availablequantity="
                            getCrossSellAvailableQuantity(
                              crossSellingProductsDetail,
                            )
                          "
                          :outofstockvisible="
                            getCrossSellOutOfStockVisible(
                              crossSellingProductsDetail,
                            )
                          "
                          :displayquantityspinner="'false'"
                          :disabled="
                            !getCrossSellOutOfStockVisible(crossSellingProductsDetail) &&
                            crossSellingProductsDetail.skus &&
                            crossSellingProductsDetail.skus[0].inventoryLevel &&
                            (crossSellingProductsDetail.skus[0].inventoryLevel
                              .inventoryLevel ===
                              InventoryStatus.OUT_OF_STOCK ||
                              crossSellingProductsDetail.skus[0].inventoryLevel
                                .inventoryLevel === InventoryStatus.STOP_SELL)
                          "
                        ></AoeAddToCartButton>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </ScrollView>
          </div>
        </div>
      </div>
      <div v-if="!props.ispopover" class="col-12 col-md-4">
        <div
          v-if="
            cart.beeNumberPayee == homeOfficeBeeNumber ||
            cart.beeNumberPayee == null
          "
          class="row p-4 divFindParty"
          data-testid="find-party-section"
        >
          <p class="connectToParty">{{ t('cart.ConnectHeader') }}</p>
          <div class="connectToPartyInfo">
            {{ t('cart.ConnectSubHeader') }}
          </div>
          <div class="mt-2 text-center">
            <KButton
              :rounded="'large'"
              class="findPartyConsultantBtn"
              @click="findPartyOrConsultantBtnHandler"
              data-testid="find-party-consultant-btn"
              >{{ t('cart.FindPartyConsultant') }}</KButton
            >
          </div>
          <div class="mt-2 text-center">
            <KCheckBox
              class="chbxParty"
              :checked="
                notOrderWithConsultantChecked != null
                  ? notOrderWithConsultantChecked
                  : false
              "
              :label="t('cart.NotOrderWithConsultant')"
              @change="onNotOrderWithConsultantChange($event)"
              data-testid="not-order-with-consultant-checkbox"
            >
            </KCheckBox>
          </div>
          <div
            v-if="showSelectPartyConsultantMessage"
            class="alert alert-warning mt-2"
            data-testid="select-party-consultant-message"
          >
            {{ t('cart.MustSelectPartyOrConsultant') }}
          </div>
        </div>
        <div
          v-if="
            cart.beeNumberPayee != homeOfficeBeeNumber &&
            cart.beeNumberPayee != null
          "
          class="row p-4 divFindParty"
        >
          <p class="OrderingWithLabel">{{ t('cart.OrderingWith') }}</p>
          <div class="row">
            <div class="col-4">
              <img
                :src="payeeSite?.photoUrl || defaultConsultantPhotoUrl"
                :alt="t('cart.ConsultantPhotoAlt')"
                class="payeePhoto"
              />
            </div>
            <div class="col-8">
              <div class="divpayeeName">
                <div class="row">
                  <div class="col-12 connectToParty">
                    {{ payeeSite?.firstName }} {{ payeeSite?.lastName }}
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 payeeLocation">
                    {{ payeeSite?.city }}, {{ payeeSite?.region }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-12 divRemoveConsultant">
              <input
                type="button"
                class="btn-link removeConsultant"
                :value="t('cart.RemoveConsultant')"
                @click="removeConsultantHandler"
              />
            </div>
          </div>
          <div
            v-if="cart.eventId != 0 && cart.eventId != undefined"
            class="row mt-2"
          >
            <div class="col-12 divAttachedToEvent">
              <p class="OrderingWithLabel">{{ t('cart.UnderParty') }}</p>
              <div class="row">
                <div class="col-12">
                  {{ eventTitle }}
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  {{ t('cart.HostedBy') }}: {{ eventHost }}
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  {{ t('cart.WhereParty') }}: {{ eventCity }}, {{ eventState }}
                </div>
              </div>
              <div class="row mt-2">
                <div class="col-12 divRemoveEvent">
                  <input
                    type="button"
                    class="btn-link removeConsultant"
                    :value="t('cart.RemoveParty')"
                    @click="removeEventHandler"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="row mt-3 p-4 orderSummaryDiv"
          data-testid="order-summary-section"
        >
          <p class="col-12 orderSumTitle">{{ t('cart.OrderSummary') }}</p>
          <div class="col-7 lblSubtotal">{{ t('cart.Subtotal') }}</div>
          <div class="col-5 lblSubtotalValue text-end" data-testid="subtotal-value">
            {{ FormatCurrency(cart.subtotal, null) }}
          </div>
          <div class="col-7 shipping-label">
            {{ t('cart.Shipping')
            }}<span v-if="freeShippingMessageText">&nbsp;*</span>
          </div>
          <div class="col-5 shipping-value text-end" data-testid="shipping-value">
            {{ FormatCurrency(cart.shippingCost, null) }}
          </div>
          <!-- <div class="col-7 lblTax">{{ t('cart.TaxCalc') }}</div>
          <div class="col-5 lblTaxValue text-end">
            <span class="pe-3">-</span>
          </div> -->
          <div class="col-7 lblTotal">{{ t('cart.Total') }}</div>
          <div class="col-5 lblTotalValue text-end" data-testid="total-value">
            {{ FormatCurrency(cart.total, null) }}
          </div>
          <AoeCartCoupon
            :cart="cart"
            data-testid="cart-coupon-component"
          ></AoeCartCoupon>
          <div class="col-12"></div>
          <div class="col-12">
            <div
              v-if="showFreeProductError"
              class="alert alert-danger mt-2"
              data-testid="free-product-error-message"
            >
              {{ t('cart.MustSelectFreeProductOrOptOut') }}
            </div>
            <KButton
              id="btnCheckout"
              ref="btnCheckout"
              class="btnCheckout"
              :rounded="'large'"
              :theme-color="'primary'"
              @click="checkoutClickedHandler"
              data-testid="checkout-btn"
              >{{ t('cart.Checkout') }}</KButton
            >
          </div>
          <BraintreeApplePayExpressCheckoutEntry
            v-if="cart && braintreeApplePayConfig"
            ref="applePayExpressRef"
            :cart="cart"
            :systemKeyword="braintreeApplePayConfig.systemKeyword"
            :parameters="{
              braintreeMerchantId: braintreeApplePayConfig.braintreeMerchantId,
              displayName: braintreeApplePayConfig.displayName,
            }"
          />

          <div
            class="col-12 pt-2 applepay"
            v-if="isApplePaySupported && braintreeApplePayConfig"
          >
            <KButton
              class="applePayButton"
              :rounded="'large'"
              :image-url="applePayCheckoutImageUrl"
              @click="applePayCheckoutClickedHandler"
              data-testid="applepay-checkout-btn"
              >{{ t('cart.ApplePayCheckout') }}</KButton
            >
          </div>
          <div class="col-12 pt-2 paypal d-none">
            <KButton
              class="payPalButton"
              :rounded="'large'"
              :image-url="paypalCheckoutImageUrl"
              data-testid="paypal-checkout-btn"
              >{{ t('cart.Checkout') }}</KButton
            >
          </div>
          <!-- Free shipping message removed - now displayed as progress bar in AoeCartDetailDiscount -->
        </div>
      </div>

      <div
        v-if="props.ispopover && crossSellingProductsDetailsSlides.length > 0"
        class="row pt-4 popOverCrosssell"
      >
        <div class="col">
          <p class="pageHeader">{{ t('cart.CompleteSet') }}</p>

          <ScrollView
            :data-items="crossSellingProductsDetailsSlides"
            :content="'content'"
            :automatic-view-change="false"
          >
            <template v-slot:content="{ props }">
              <div
                class="row popOverCrossSellProdInfo"
                v-for="crossSellingProductsDetail in props.item[0].product"
                :key="crossSellingProductsDetail.productCode"
              >
                <div class="col-12 col-sm-3 crossSellProdDiv">
                  <a
                    :href="
                      GetFullProductUrlFromStringDictionary(
                        crossSellingProductsDetail.productCode,
                      )
                    "
                    :data-category-code="crossSellingProductsDetail.productCode"
                    draggable="false"
                  >
                    <img
                      :src="
                        GetFullProductImageUrlFromStringDictionary(
                          crossSellingProductsDetail.productCode,
                        )
                      "
                      class="crossSellImage"
                      :alt="t('cart.ProductImageAlt')"
                      draggable="false"
                    />
                  </a>
                </div>
                <div class="col-12 col-sm-9 popoverCrossSellProdNameDiv">
                  <div class="row">
                    <div class="col-12 popoverCrossSellProductName">
                      {{ crossSellingProductsDetail.productName }}
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12 popoverCrossSellProductCode">
                      #{{ crossSellingProductsDetail.productCode }}
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12 col-sm-4 completeSetPrice">
                      {{
                        FormatCurrency(
                          getCrossSellRetailPrice(crossSellingProductsDetail),
                          null,
                        )
                      }}
                    </div>
                    <div class="col-12 col-sm-8 completeSetAddToCart">
                      <AoeAddToCartButton
                        :ordertypeid="props.ordertypeid"
                        :skucode="
                          getCrossSellSkuCode(crossSellingProductsDetail)
                        "
                        :availablequantity="
                          getCrossSellAvailableQuantity(
                            crossSellingProductsDetail,
                          )
                        "
                        :outofstockvisible="
                          getCrossSellOutOfStockVisible(
                            crossSellingProductsDetail,
                          )
                        "
                        :displayquantityspinner="'false'"
                        :disabled="
                          !getCrossSellOutOfStockVisible(crossSellingProductsDetail) &&
                          crossSellingProductsDetail.skus &&
                          crossSellingProductsDetail.skus[0].inventoryLevel &&
                          (crossSellingProductsDetail.skus[0].inventoryLevel
                            .inventoryLevel === InventoryStatus.OUT_OF_STOCK ||
                            crossSellingProductsDetail.skus[0].inventoryLevel
                              .inventoryLevel === InventoryStatus.STOP_SELL)
                        "
                      ></AoeAddToCartButton>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </ScrollView>
        </div>
      </div>

      <div v-if="props.ispopover" class="row mt-3 p-4 popOverSummaryDiv">
        <div class="col-8 lblSubtotal">{{ t('cart.Subtotal') }}</div>
        <div class="col-4 lblSubtotalValue text-end">
          {{ FormatCurrency(cart.subtotal, null) }}
        </div>
        <div class="col-8 shipping-label">
          {{ t('cart.Shipping') }}
          <span v-if="freeShippingMessageText">&nbsp;*</span>
        </div>
        <div class="col-4 shipping-value text-end">
          {{ FormatCurrency(cart.shippingCost, null) }}
        </div>
        <!-- <div class="col-7 lblTax">{{ t('cart.TaxCalc') }}</div>
        <div class="col-5 lblTaxValue text-end">
          <span class="pe-3">-</span>
        </div> -->
        <div class="col-12"></div>
        <div class="col-12">
          <button
            class="btnViewCart btn primaryBtn w-100"
            @click="viewCartClickedHandler"
          >
            {{ t('cart.ViewCart') }}
          </button>
        </div>
        <!-- Free shipping message removed - now displayed as progress bar in AoeCartDetailDiscount -->
      </div>
    </div>
    <!-- Empty cart message -->
    <div
      v-if="isCartEmpty"
      class="empty-cart-message"
      data-testid="empty-cart-message"
    >
      <div class="empty-cart-icon">🛒</div>
      <h3 class="empty-cart-title">{{ t('cart.EmptyCart') }}</h3>
      <p class="empty-cart-text">{{ t('cart.EmptyCartMessage') }}</p>
    </div>

    <Popup
      :anchor="'btnCheckout'"
      :show="showCheckoutPopup"
      :popup-class="'aoe-checkout-popup-content'"
      :append-to="'body'"
      data-testid="checkout-popup"
    >
      <div class="row mt-3">
        <div class="col-md-6 popover-signin">
          <p class="pageHeader">{{ t('cart.SignIn') }}</p>
          <p class="pageSubSubHeader">{{ t('cart.SignInSubHeader') }}</p>
          <KButton
            id="btnSignIn"
            ref="btnSignIn"
            class="btnSignIn"
            :rounded="'large'"
            :theme-color="'primary'"
            @click="signInClickedHandler"
            data-testid="signin-btn"
            >{{ t('cart.SignIn') }}</KButton
          >
        </div>
        <div class="col-md-6 popover-guest">
          <p class="pageHeader">{{ t('cart.GuestCheckout') }}</p>
          <p class="pageSubSubHeader">{{ t('cart.GuestCheckoutSubHeader') }}</p>
          <KButton
            id="btnCheckoutGuest"
            ref="btnCheckoutGuest"
            class="btnCheckoutGuest"
            :rounded="'large'"
            :theme-color="'primary'"
            @click="checkoutGuestClickedHandler"
            data-testid="guest-checkout-btn"
            >{{ t('cart.GuestCheckout') }}</KButton
          >
        </div>
        <div class="close-popup">
          <KButton
            :fill-mode="'clear'"
            @click="closeCheckoutPopupHandler"
            class="popupCloseButton"
            >&#x2715;</KButton
          >
        </div>
      </div>
    </Popup>

    <Popup
      :anchor="'btnConnectToParty'"
      :show="showConnectToPartyPopup"
      :popup-class="'aoe-connect-party-popup-content'"
      :append-to="'body'"
    >
      <AoeCartPopupEventLocator
        @close-popup="closeCheckoutPopupHandler"
        :cart="cart"
        :ispopover="props.ispopover"
        :isPartyOnly="showOnlyConnectToPartyPopup"
        :eventHosts="eventHosts"
      >
      </AoeCartPopupEventLocator>
    </Popup>
  </div>
</template>

<style lang="scss">
.aoe-cart-popup-overlay {
  content: '' !important;
  display: block !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background-color: rgba(black, 0.5) !important;
  z-index: $z-kendo-overlay !important;
  pointer-events: auto !important;
}

.aoe-cart-detail-container
  .k-animation-container:has(.aoe-checkout-popup-content),
.aoe-cart-detail-container
  .k-animation-container:has(.aoe-connect-party-popup-content) {
  z-index: $z-kendo-popup !important;
}
.popOverDeleteItemButtonDiv {
  width: 100%;
  text-align: right;
}
.completeSetAddToCart button {
  padding: $spacing-1 $spacing-2-5 !important;
}

.completeSetPrice input {
  @include typography-body;
  text-align: left !important;
}

/* Popover cross-sell mobile improvements */
.popOverCrossSellProdInfo {
  margin-bottom: $spacing-3;

  .popoverCrossSellProductName,
  .popoverCrossSellProductCode {
    word-break: break-word;
    overflow-wrap: anywhere;
    white-space: normal;
  }

  .popoverCrossSellProductName {
    @include typography-body;
    @include font-weight-semibold;
  }

  .crossSellImage {
    max-width: 100%;
    height: auto;
  }

  .completeSetAddToCart {
    .aoe-add-to-cart-button,
    button {
      width: 100%;
    }
  }
}

@media (max-width: 575.98px) {
  .popOverCrossSellProdInfo {
    .completeSetPrice {
      margin-bottom: $spacing-2;
    }
  }

  .popOverSummaryDiv {
    padding: $spacing-3 !important;

    .lblSubtotal,
    .shipping-label,
    .lblSubtotalValue,
    .shipping-value {
      margin-bottom: $spacing-2;
    }
  }
}

.btnFindPartyCancelDiv {
  text-align: right;
}

.aoe-connect-party-popup-content {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  padding: $spacing-2-5;
  transform: translate(-50%, -50%) !important;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  pointer-events: auto !important;
  z-index: $z-kendo-popup-top !important;
}

.hostAutoComplete,
.consultantAutoComplete {
  width: 100%;
}

.aoe-checkout-popup-content {
  position: fixed !important;
  top: 50vh !important;
  left: 50vw !important;
  padding: $spacing-2-5;
  transform: translate(-50%, -50%) !important;
  max-width: 90vw;
  max-height: 90vh;
  background: white;
  border-radius: $radius-md;
  box-shadow: 0 4px 6px rgba(black, 0.1);
  pointer-events: auto !important;
  z-index: $z-kendo-popup-top !important;
}

.popupCloseButton {
  color: black;
  transform: scale(1.5);
}

.btnCheckoutGuest,
.btnSignIn,
.btnFindPartyConfirm,
.btnFindPartyCancel {
  @include typography-body;
  @include font-weight-semibold;
  display: flex;
  height: 48px;
  padding: $spacing-4 $spacing-12;
  justify-content: center;
  align-items: center;
  gap: $gap-sm;
  align-self: stretch;
  border-radius: $radius-md;
  border-color: #2c7a87;
  background: #2c7a87;
  color: var(--Main-White, white);
  font-style: normal;
  text-transform: uppercase;
  min-width: 75%;
  width: fit-content;
  max-width: 100%;
  margin-top: 20px;
}

.btnFindPartyConfirm,
.btnFindPartyCancel {
  width: 100%;
}

.btnFindPartyCancel {
  border-color: black;
  background: white;
  color: black;
}

.close-popup {
  text-align: right;
  margin-top: -10px;
  position: absolute;
  right: 10px;
  top: 10px;
}

.popover-guest {
  border-left: solid 1px #e6e8e6;
  padding-left: 25px;
  padding-right: 25px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.popover-signin {
  padding-left: 25px;
  padding-right: 25px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.aoe-checkout-popup-content .row {
  align-items: stretch;
}

.aoe-checkout-popup-content .pageHeader {
  margin-top: 0;
  margin-bottom: 12px;
}

.aoe-checkout-popup-content .pageSubSubHeader {
  margin-top: 0;
  margin-bottom: 12px;
  flex-grow: 1;
}
.aoe-cart-detail.aoe-cart-detail-container.container {
    padding: 0px;
}

@media screen and (min-width: 769px) {
  .popover-guest,
  .popover-signin {
    min-height: 100%;
  }

  .btnCheckoutGuest,
  .btnSignIn {
    margin-top: auto;
  }
}

/* Mobile responsive styles for popups */
@media screen and (max-width: 768px) {
  .aoe-checkout-popup-content {
    width: 95vw;
    max-width: 95vw;
    padding: $spacing-5 $spacing-4;
    top: 50%;
  }
  .cartDrawer img {
    width: 150px !important;
  }
  .cartItems {padding:25px;}
  .popOverCrosssell .k-scrollview-view{
    overflow:scroll;
  }

  .aoe-checkout-popup-content .row {
    margin: 0 !important;
    align-items: flex-start;
  }

  .popover-guest,
  .popover-signin {
    border-left: none;
    padding-left: 15px;
    padding-right: 15px;
    padding-top: 20px;
    margin-top: 0;
    margin-bottom: 0;
  }

  .popover-signin {
    margin-bottom: 20px;
  }

  .popover-guest {
    border-top: solid 1px #e6e8e6;
    margin-top: 0;
    margin-bottom: 0;
  }

  .btnCheckoutGuest,
  .btnSignIn {
    @include typography-small;
    width: 100%;
    padding: $spacing-3 $spacing-4;
    margin-top: $spacing-4;
    height: auto;
    min-height: 48px;
    white-space: normal;
    word-wrap: break-word;
  }

  .popover-guest,
  .popover-signin {
    align-items: stretch;
  }

  .close-popup {
    right: 5px;
    top: 5px;
  }

  .popupCloseButton {
    transform: scale(1.2);
  }

  .aoe-checkout-popup-content .pageHeader {
    @include typography-h4;
    margin-top: 0;
    margin-bottom: 10px;
  }

  .aoe-checkout-popup-content .pageSubSubHeader {
    @include typography-small;
    margin-top: 0;
    margin-bottom: 10px;
  }

  .aoe-connect-party-popup-content {
    width: 95vw;
    max-width: 95vw;
    padding: $spacing-5 $spacing-4;
  }

  /* Fix close button position in mobile */
  .aoe-connect-party-popup-content .closePopup {
    position: absolute;
    right: 10px;
    top: 10px;
    width: auto;
    padding: 0;
  }

  /* Make Find Consultant section full width in mobile */
  .aoe-connect-party-popup-content .popOverGuest {
    width: 100%;
    max-width: 100%;
    flex: 0 0 100%;
    border-left: none;
    border-top: solid 1px #e6e8e6;
    padding-top: 20px;
    margin-top: 20px;
  }

  /* Adjust Find Party section width in mobile */
  .aoe-connect-party-popup-content .popOverSignIn {
    width: 100%;
    max-width: 100%;
    flex: 0 0 100%;
    padding-right: 40px; /* Space for close button */
  }

  /* Fix button text overflow in mobile - keep in same row */
  .aoe-connect-party-popup-content .btnFindPartyConfirm,
  .aoe-connect-party-popup-content .btnFindPartyCancel {
    @include typography-caption;
    padding: $spacing-2-5 $spacing-1;
    white-space: normal;
    word-wrap: break-word;
    line-height: 1.2;
    height: auto;
    min-height: 44px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Adjust button container widths in mobile */
  .aoe-connect-party-popup-content .btnFindPartyCancelDiv,
  .aoe-connect-party-popup-content .btnFindPartyConfirmDiv {
    flex: 0 0 50% !important;
    max-width: 50% !important;
    padding: 0 5px;
  }

  .aoe-connect-party-popup-content .row {
    margin-left: 0;
    margin-right: 0;
  }

  /* Hide empty col-6 and force buttons row layout */
  .aoe-connect-party-popup-content .row:last-child {
    display: flex !important;
    flex-wrap: nowrap !important;
  }

  .aoe-connect-party-popup-content .row:last-child .col-6:empty {
    display: none !important;
  }
}

@media screen and (max-width: 480px) {
  .aoe-checkout-popup-content {
    width: 98vw;
    max-width: 98vw;
    padding: $spacing-4 $spacing-2-5;
  }

  .btnCheckoutGuest,
  .btnSignIn {
    @include typography-caption;
    padding: $spacing-2-5 $spacing-3;
    height: auto;
    min-height: 44px;
    white-space: normal;
    word-wrap: break-word;
    line-height: 1.3;
  }

  .pageHeader {
    @include typography-body-large;
  }

  .pageSubSubHeader {
    @include typography-small;
  }

  /* Extra small screens - connect party popup */
  .aoe-connect-party-popup-content {
    width: 98vw;
    max-width: 98vw;
    padding: $spacing-4 $spacing-2-5;
  }

  .aoe-connect-party-popup-content .btnFindPartyConfirm,
  .aoe-connect-party-popup-content .btnFindPartyCancel {
    @include typography-caption;
    padding: $spacing-2 $spacing-1;
    height: auto;
    min-height: 44px;
    white-space: normal;
    word-wrap: break-word;
    line-height: 1.1;
  }

  .aoe-connect-party-popup-content .popOverSignIn {
    padding-right: 35px;
  }

  /* Force buttons in same row for extra small screens */
  .aoe-connect-party-popup-content .btnFindPartyCancelDiv,
  .aoe-connect-party-popup-content .btnFindPartyConfirmDiv {
    flex: 0 0 50% !important;
    max-width: 50% !important;
    padding: 0 3px;
  }

  .aoe-connect-party-popup-content .row {
    margin-left: 0;
    margin-right: 0;
  }

  .aoe-connect-party-popup-content .row:last-child {
    display: flex !important;
    flex-wrap: nowrap !important;
  }

  .aoe-connect-party-popup-content .row:last-child .col-6:empty {
    display: none !important;
  }
}

.aoe-cart-detail {
  .removeConsultant {
    background: none !important;
    border: none;
    @include typography-body;
    padding: 0 !important;
    color: #069;
    text-decoration: underline;
    cursor: pointer;
  }

  .OrderingWithLabel {
    @include typography-body-large;
  }

  .payeeLocation {
    @include typography-body;
  }

  .paypal .k-image {
    margin-top: 3px;
    margin-right: 5px;
    height: 16px;
    width: auto;
  }

  .applepay .k-image {
    margin-top: 3px;
    margin-right: 5px;
    height: 16px;
    width: auto;
  }

  .quantity {
    width: 50px;
  }
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }
  input[type='number'] {
    appearance: textfield;
  }
  .k-numerictextbox .k-input-inner {
    text-align: right;
    @include font-weight-bold;
    color: none;
  }
  .k-numerictextbox .k-input-inner[disabled] {
    color: black;
  }
  .k-button-text {
    @include font-weight-bold;
  }
  .crosssell .k-numerictextbox .k-input-inner {
    text-align: center;
  }

  .crosssell .productName {
    text-align: center;
  }

  .k-input-flat {
    border-bottom-color: black !important;
  }

  .continueShoppingButton {
    padding: $card-padding;
    text-align: center;
    @include typography-body;
    @include font-weight-semibold;
    font-style: normal;
    line-height: 24px; /* 150% */
    text-transform: uppercase;
  }

  .cartSkuName {
    @include typography-body;
    @include font-weight-semibold;
    font-style: normal;
  }

  .cartSkuCode {
    @include typography-caption;
    @include font-weight-regular;
    color: rgba(#282727, 0.7);
    font-style: normal;
  }

  .cartItemPrice {
    @include typography-h4;
    @include font-weight-medium;
    font-style: normal;
    letter-spacing: 0.6px;
  }

  .minus,
  .plus {
    border: none;
    @include typography-h3;
    background-color: white !important;
    padding: $spacing-2-5;
    vertical-align: middle !important;
  }

  .quantity input {
    text-align: center !important;
  }

  .crosssell, .mobileCrossSell {
    .crossSellProdInfo {
      img {
        max-height: 160px;
        object-fit: cover;
      }
    }
  }

  @media screen and (max-width: 768px) {
    .nonMobileCartItems,
    .crosssell,
    .continueShoppingButton {
      display: none;
    }

    .mobileCrossSell .k-scrollview-elements {
      padding: $spacing-5 0 0 0;
    }

    .mobileCrossSell .pageHeader {
      margin: 0;
      text-wrap-mode: wrap;
    }
  }

  @media screen and (min-width: 769px) {
    .allCartItems {
      padding-right: 50px;
    }

    .mobileCartItems {
      display: none;
    }

    /* Popover mode: reduce padding to fit drawer width */
    .aoe-cart-popup-overlay .allCartItems {
      padding-right: $spacing-2 !important;
      padding-left: $spacing-2 !important;
    }

    .mobileCrossSell {
      display: none;
    }

    .crosssell .k-scrollview-elements {
      right: 0px !important;
    }
  }

  .connectToParty {
    @include typography-h4;
    @include font-weight-semibold;
    font-style: normal;
    line-height: 16px;
  }

  .connectToPartyInfo {
    @include typography-small;
    @include font-weight-regular;
    font-style: normal;
    line-height: 150%;
  }

  .findPartyConsultantBtn,
  .findPartyBtn {
    text-align: center;
    @include typography-body;
    @include font-weight-semibold;
    font-style: normal;
    line-height: 24px; /* 150% */
    text-transform: uppercase;
    border-radius: $radius-md;
    background: #b0d3dc;
    padding: $spacing-4;
  }

  .findPartyBtn {
    width: 75%;
  }

  .divFindParty {
    border-radius: $radius-md;
    border: 2px solid #ead9cf;
    background: #fbf7f5;
    padding: $spacing-12;
  }

  .chbxParty {
    @include typography-small;
    @include font-weight-regular;
    font-style: normal;
  }

  .chbxParty input {
    border-radius: $radius-sm;
    margin: $spacing-2-5;
  }

  .chbxParty input:not(:checked) {
    background: var(--Lara-Input-Field-inputBg, white);
  }

  .pageHeader {
    @include typography-h3;
    @include font-weight-medium;
    color: #282727;
    font-style: normal;
  }

  .orderSummaryDiv {
    border-radius: $radius-md;
    border: 2px solid #e6e8e6;
    font-style: normal;
  }

  .orderSumTitle {
    @include typography-h4;
    @include font-weight-semibold;
    line-height: 16px;
  }

  .lblSubtotal,
  .lblSubtotalValue,
  .shipping-label,
  .lblTax,
  .lblTotal {
    @include typography-body;
    @include font-weight-medium;
  }

  .lblSubtotal,
  .shipping-label,
  .lblTax,
  .lblTotal {
    line-height: 24px;
  }

  .lblSubtotalValue,
  .shipping-value,
  .lblTaxValue,
  .lblTotalValue {
    text-align: right;
    @include font-weight-semibold;
  }

  .btnCheckout,
  .btnCheckoutGuest,
  .btnSignIn {
    display: flex;
    height: 48px;
    padding: $spacing-4 $spacing-12;
    justify-content: center;
    align-items: center;
    gap: $gap-sm;
    align-self: stretch;
    border-radius: $radius-md;
    border-color: #2c7a87;
    background: #2c7a87;
    color: var(--Main-White, white);
    text-align: center;
    @include typography-body;
    @include font-weight-semibold;
    font-style: normal;
    text-transform: uppercase;
    width: 100%;
    margin-top: 20px;
  }

  .payPalButton {
    margin-top: 20px;
    width: 100%;
    @include typography-body;
    @include font-weight-semibold;
    font-style: normal;
    line-height: 24px; /* 150% */
    text-transform: uppercase;
    border-radius: $radius-md;
    border: 1px solid #282727;
    background: var(--vt-c-white);
    padding: $spacing-4 $spacing-12;
  }

  .applePayButton {
    display: flex;
    height: 48px;
    padding: $spacing-4 $spacing-12;
    justify-content: center;
    align-items: center;
    gap: $gap-sm;
    align-self: stretch;
    border-radius: $radius-md;
    border: 1px solid var(--color-text);
    background: var(--color-text);
    color: var(--color-background);
    text-align: center;
    @include typography-body;
    @include font-weight-semibold;
    text-transform: uppercase;
    width: 100%;
    margin-top: $spacing-0;
  }

  .applePayButton .k-button-text,
  .applePayButton .k-button-icon,
  .applePayButton .k-button-image {
    flex: 0 0 auto;
  }

  .applePayButton .k-button-image {
    height: $spacing-6;
    width: auto;
    object-fit: contain;
  }

  .nonMobileCartItems .prodImage {
    display: flex;
    width: 90px;
    height: 90px;
    justify-content: center;
    align-items: center;
  }

  .cartItems {
    border-bottom: 1px solid #e0e0e0;
  }

  .cartItemInfo {
    margin: auto;
  }

  .cartItemInfoQty {
    margin: auto;
    white-space: nowrap;
  }

  .k-scrollview {
    height: 400px;
    border: none;
  }

  .crossSellImage {
    border-radius: $radius-md;
  }

  .crossSellProductName,
  .crossSellPrice {
    @include typography-body;
    @include font-weight-semibold;
    font-style: normal;
    text-wrap: auto;
    text-align: center;
  }

  .crossSellReviewStars {
    margin: auto;
  }

  .k-scrollview-next,
  .k-scrollview-prev {
    color: black;
  }

  .crossSellProdInfo {
    display: flex;
    flex-flow: column;
    justify-content: space-between;
  }

  .cartItemInfoMobile {
    padding-top: 10px;
    padding-bottom: 10px;
  }

  .cartItemInfoMobileQty {
    white-space: nowrap;
  }

  .number .k-textbox {
    width: 40%;
  }

  .number .k-textbox .k-input-inner {
    text-align: center;
  }

  .k-input {
    vertical-align: -webkit-baseline-middle !important;
  }

  @media (min-width: 1025px) {
    .k-scrollview-wrap > .k-scrollview-view {
      display: flow-root !important;
    }
  }

  .aoe-cart-coupon .k-button-link {
    padding: 0 !important;
  }
}

.crossSellProdInfo {
  position: relative;

  .wishListBtnArea {
    position: absolute;
    right: 32px;
    top: 7px;
  }

  .btnwishListHeart {
    background-color: var(--color-background) !important;
    border: none !important;
    color: var(--color-text) !important;
  }
}

/* Empty cart message styles */
.empty-cart-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: $spacing-16 $spacing-5;
  text-align: center;
  min-height: 400px;
}

.empty-cart-icon {
  @include typography-display;
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty-cart-title {
  @include typography-h3;
  @include font-weight-semibold;
  color: #2d3436;
  margin-bottom: 12px;
}

.empty-cart-text {
  @include typography-body;
  color: #636e72;
  margin-bottom: 30px;
  max-width: 400px;
}

.continue-shopping-btn {
  min-width: 200px;
}

/* Cart header with close button for popover */
.cart-header-with-close {
  display: flex;
  align-items: center;
  gap: $spacing-3;
  margin-bottom: 0;
}

.close-cart-button {
  background: none;
  border: none;
  padding: $spacing-1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #282727;
  transition: all 0.2s ease;
  border-radius: $radius-sm;
}

.close-cart-button:hover {
  background-color: var(--vt-c-white-soft);
  color: #000;
}

.close-cart-button:active {
  transform: scale(0.95);
}

.close-cart-button svg {
  width: 32px;
  height: 32px;
}

.cart-header-with-close .pageHeader {
  margin: 0;
  flex: 1;
}
</style>
